import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../api/axiosInstance";

const scoreColor = (score) => {
  if (score >= 70) return "#22c55e";
  if (score >= 40) return "#f59e0b";
  return "#ef4444";
};

const recommendationBadge = {
  "Strong Hire": { bg: "#dcfce7", color: "#16a34a" },
  "Hire":        { bg: "#dbeafe", color: "#1d4ed8" },
  "Maybe":       { bg: "#fef9c3", color: "#a16207" },
  "No Hire":     { bg: "#fee2e2", color: "#dc2626" },
};

const PastInterviewsPage = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/api/interview/my-sessions")
      .then(({ data }) => setSessions(data))
      .catch((err) => setError("Failed to load past interviews."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-muted-foreground">Loading your interviews...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <p className="text-error">{error}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-1">
              My Past Interviews
            </h1>
            <p className="text-muted-foreground">
              {sessions.length} interview{sessions.length !== 1 ? "s" : ""} completed
            </p>
          </div>
          <button
            onClick={() => navigate("/candidate/interview-dashboard")}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            + New Interview
          </button>
        </div>

        {/* Empty state */}
        {sessions.length === 0 && (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">📋</div>
            <h2 className="text-xl font-semibold text-foreground mb-2">
              No interviews yet
            </h2>
            <p className="text-muted-foreground mb-6">
              Complete your first interview to see results here.
            </p>
            <button
              onClick={() => navigate("/candidate/interview-dashboard")}
              className="px-6 py-3 bg-primary text-white rounded-lg"
            >
              Start Interview
            </button>
          </div>
        )}

        {/* Interview list */}
        <div className="space-y-4">
          {sessions.map((session) => {
            const badge = recommendationBadge[session.recommendation]
              || recommendationBadge["Maybe"];

            return (
              <div
                key={session._id}
                className="bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">

                  {/* Left — Position + Date */}
                  <div className="flex items-center gap-4">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                      style={{ backgroundColor: scoreColor(session.overallScore) }}
                    >
                      {session.overallScore}
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-foreground">
                        {session.position}
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {session.experience} years exp • {" "}
                        {new Date(session.date).toLocaleDateString("en-US", {
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                  </div>

                  {/* Right — Badge + Button */}
                  <div className="flex items-center gap-4">
                    <span
                      className="px-3 py-1 rounded-full text-sm font-semibold"
                      style={{ background: badge.bg, color: badge.color }}
                    >
                      {session.recommendation}
                    </span>
                    <button
                      onClick={() =>
                        navigate("/candidate/interview-results-hub", {
                          state: { sessionId: session.sessionId },
                        })
                      }
                      className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90"
                    >
                      View Result →
                    </button>
                  </div>

                </div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default PastInterviewsPage;